import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Shield,
  Zap,
  Mail,
  Phone,
  User,
  Calendar,
  UserCheck,
} from "lucide-react";

import Layout from "../components/Layout";
import PageBanner from "../components/PageBanner";
import { computeSavings } from "../components/SavingsCalculator";
import BusinessDayPicker from "../components/BusinessDayPicker";
import { api, formatApiError } from "../lib/api";
import { trackEnrollmentStart, trackEnrollmentComplete } from "../lib/ga";

import Field from "../components/enroll/Field";
import EnrollStepper from "../components/enroll/EnrollStepper";
import EnrollSidebar from "../components/enroll/EnrollSidebar";
import ReviewView from "../components/enroll/ReviewView";
import ConfirmationView from "../components/enroll/ConfirmationView";
import { ENROLL_COMPLETE_STORAGE_KEY } from "./EnrollmentComplete";
import OtpModal from "../components/enroll/OtpModal";

/* ============================================================
   Enroll — orchestrator for the residential signup flow.

   Architecture:
     - State + side-effects live here.
     - Visual components live in /components/enroll.
     - Flow: "datos" → "review" → OTP modal → "submitted".
   ============================================================ */

// Plan catalog — Rhythm-style: 3 plans by TYPE (not by size), shared across
// service types. Pricing varies by ZIP + serviceType (see dynamicRate).
// kWh reference is per service type: apt=1000, residential=2000, com=3000.
const SERVICE_USAGE_KWH = { apartments: 1000, residential: 2000, commercial: 3000 };

const PLAN_CATALOG = [
  { id: "saver12",  name: "Velcor Saver 12",  term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "eco12",    name: "Velcor Eco 12",    term: "12 meses · 100% renovable",       baseRate: 12.4 },
  { id: "secure24", name: "Velcor Secure 24", term: "24 meses · Máxima estabilidad",   baseRate: 11.9 },
  // Legacy IDs from older flows mapped to the new default plan.
  { id: "res_small",  name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "res_med",    name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "res_large",  name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "apt_studio", name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "apt_1br",    name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "apt_2br",    name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "com_small",  name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "com_med",    name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "com_large",  name: "Velcor Saver 12", term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "flex12",     name: "Velcor Saver 12",  term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "flex24",     name: "Velcor Secure 24", term: "24 meses · Máxima estabilidad",   baseRate: 11.9 },
  { id: "green",      name: "Velcor Eco 12",    term: "12 meses · 100% renovable",       baseRate: 12.4 },
  { id: "flexible",   name: "Velcor Saver 12",  term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "plus",       name: "Velcor Saver 12",  term: "12 meses · Tarifa fija más baja", baseRate: 10.9 },
  { id: "elite",      name: "Velcor Secure 24", term: "24 meses · Máxima estabilidad",   baseRate: 11.9 },
];

function hash01(s) {
  let h = 2166136261;
  const str = String(s || "default");
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

function dynamicRate(planId, baseRate, zip, utility, serviceType) {
  const seed = `${zip || "75001"}|${utility || "Oncor"}|${planId}`;
  const r = hash01(seed);
  const variance = (r - 0.5) * 1.6; // ±0.8¢
  const surcharge =
    serviceType === "commercial" ? 2.5 : serviceType === "apartments" ? 0.4 : 0;
  return Math.max(8.5, Math.min(18.0, baseRate + variance + surcharge));
}

export default function Enroll() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const zip = params.get("zip") || localStorage.getItem("velcor_zip") || "";
  const address = params.get("address") || localStorage.getItem("velcor_service_address") || "";
  const unit = params.get("unit") || localStorage.getItem("velcor_unit") || "";
  const city = params.get("city") || localStorage.getItem("velcor_city") || "Houston";
  const esiidParam = params.get("esiid") || localStorage.getItem("velcor_esiid") || "";
  const utilityParam = params.get("utility") || localStorage.getItem("velcor_utility") || "";
  const serviceType = params.get("type") || localStorage.getItem("velcor_service_type") || "residential";
  const selectedPlanInitial = params.get("plan") || localStorage.getItem("velcor_selected_plan") || "saver12";
  const [selectedPlan] = useState(selectedPlanInitial);

  const _kwh = SERVICE_USAGE_KWH[serviceType] || 2000;
  const _basePlan = PLAN_CATALOG.find((p) => p.id === selectedPlan) || PLAN_CATALOG[0];
  const currentPlan = {
    ..._basePlan,
    rate: Number(dynamicRate(_basePlan.id, _basePlan.baseRate, zip, utilityParam, serviceType).toFixed(1)),
    kwh: _kwh,
  };

  const [estimate_unused_legacy, setEstimate_unused_legacy] = useState(null); // kept for shape compat
  // Derived view of monthly kWh estimate. Was a useEffect + setState combo
  // that triggered the linter's set-state-in-effect rule; useMemo keeps
  // the value reactive without the extra render cycle.
  const estimate = useMemo(() => {
    const monthlyKwh = currentPlan.kwh || 2000;
    return { annual_kwh: monthlyKwh * 12, monthly_kwh: monthlyKwh };
  }, [currentPlan.kwh]);
  void estimate_unused_legacy; void setEstimate_unused_legacy;
  const currentBill = Number(localStorage.getItem("velcor_current_bill")) || null;

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const [form, setForm] = useState({
    nombre: "",
    apellido: "",
    telefono: "",
    email: "",
    fecha_nacimiento: "",
    phone_is_contact: true,
    numero_cuenta: "",
    fecha_inicio: "",
    notas: "",
    referral_code: (
      new URLSearchParams(window.location.search).get("ref") ||
      localStorage.getItem("velcor_ref") ||
      ""
    )
      .toUpperCase()
      .trim(),
  });

  // Flow state: "datos" → "review" → OTP modal opens → "submitted"
  const [step, setStep] = useState("datos");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // ── Clean URL on mount (Jun 2026) ──────────────────────────────────────
  // Visitors arrive at /enroll?zip=...&address=...&esiid=...&plan=...&...
  // Once we've copied the query into localStorage (already used as the
  // fallback above) we replace the URL with a clean "/enroll" so the
  // address bar looks professional and SMS/email shares stay short.
  // history.replaceState bypasses React Router so it doesn't re-render
  // and `params` (already read above) keeps the original values for
  // this session.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (sp.toString() === "") return;
    const persist = {
      zip: "velcor_zip",
      address: "velcor_service_address",
      unit: "velcor_unit",
      city: "velcor_city",
      esiid: "velcor_esiid",
      utility: "velcor_utility",
      type: "velcor_service_type",
      plan: "velcor_selected_plan",
      ref: "velcor_ref",
    };
    Object.entries(persist).forEach(([qkey, lsKey]) => {
      const v = sp.get(qkey);
      if (v) {
        try { localStorage.setItem(lsKey, v); } catch { /* private mode */ }
      }
    });
    // Preserve only utm_* tracking params (campaign attribution still works)
    const kept = new URLSearchParams();
    sp.forEach((v, k) => { if (k.startsWith("utm_")) kept.append(k, v); });
    const cleanUrl = "/enroll" + (kept.toString() ? `?${kept.toString()}` : "");
    try { window.history.replaceState(null, "", cleanUrl); } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Inline fraud validation (Feb 2026) ────────────────────────────────
  // Email + phone are validated server-side on blur (and re-checked before
  // submit). The error sits directly under the input so the visitor sees
  // the issue without scrolling or waiting for a 403. Submit is gated
  // until both come back ok=true. Cache results so re-validating on
  // every keystroke is unnecessary.
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [emailChecking, setEmailChecking] = useState(false);
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [dobError, setDobError] = useState("");

  /**
   * Inline 18+ check. Accepts the DateMaskedInput's "MM/DD/YYYY" format
   * (or any locale-parseable date). Returns true only when the visitor
   * is at least 18 years old at submission time, the same threshold the
   * TDSP requires for retail electricity enrollment. We compute the age
   * client-side because waiting for backend would slow the UX and the
   * DOB is the user's own input — no security risk to surface inline.
   */
  const validateDobInline = (raw) => {
    const v = (raw || "").trim();
    if (!v) { setDobError(""); return false; }
    // Try both MM/DD/YYYY and ISO. Reject obviously bad lengths.
    let d = null;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
      const [mm, dd, yyyy] = v.split("/");
      d = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
    } else {
      d = new Date(v);
    }
    if (!d || isNaN(d.getTime())) {
      setDobError("Fecha no válida.");
      return false;
    }
    const today = new Date();
    let age = today.getFullYear() - d.getFullYear();
    const mDiff = today.getMonth() - d.getMonth();
    if (mDiff < 0 || (mDiff === 0 && today.getDate() < d.getDate())) age--;
    if (age < 18) {
      setDobError("Debes ser mayor de 18 años.");
      return false;
    }
    if (age > 120) {
      setDobError("Fecha no válida.");
      return false;
    }
    setDobError("");
    return true;
  };

  const validateEmailInline = async (raw) => {
    const v = (raw || "").trim();
    if (!v) { setEmailError(""); return true; }
    setEmailChecking(true);
    try {
      const { data } = await api.post("/validate/email", { email: v });
      const ok = !!data?.ok;
      setEmailError(ok ? "" : (data?.message || "Correo electrónico no válido."));
      return ok;
    } catch {
      setEmailError("");
      return true; // Permissive on network errors — backend will re-check on submit.
    } finally {
      setEmailChecking(false);
    }
  };

  const validatePhoneInline = async (raw) => {
    const digits = (raw || "").replace(/\D+/g, "");
    if (digits.length < 10) {
      setPhoneError(digits.length === 0 ? "" : "Número no válido para verificación.");
      return false;
    }
    setPhoneChecking(true);
    try {
      const { data } = await api.post("/validate/phone", { phone: digits });
      const ok = !!data?.ok;
      setPhoneError(ok ? "" : (data?.message || "Número no válido para verificación."));
      return ok;
    } catch {
      setPhoneError("");
      return true; // Permissive on network errors.
    } finally {
      setPhoneChecking(false);
    }
  };

  // ── Address-first guard (Feb 2026) ─────────────────────────────────────
  // /enroll requires an ESIID + utility to compute accurate pricing and to
  // make a complete lead (the lead doc carries the ESIID/utility through
  // the rest of the activation pipeline). If the visitor lands here from
  // an external ad without going through /address, bounce them to /address
  // with `next=/enroll` + the current query string preserved so they pick
  // up exactly where they left off.
  useEffect(() => {
    if (esiidParam) return;
    const sp = new URLSearchParams();
    sp.set("next", "/enroll");
    sp.set("reason", "enroll");
    if (serviceType) sp.set("type", serviceType);
    if (params.get("plan")) sp.set("plan", params.get("plan"));
    navigate(`/address?${sp.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esiidParam]);

  // Kept for future UI surface (e.g. inline "Ahorras $X/año" badge). Not
  // currently rendered but cheap to compute and keeps parity with /residential.
  // eslint-disable-next-line no-unused-vars
  const _savings = estimate?.estimated_monthly_bill
    ? computeSavings({ estimate, plan: selectedPlan, currentBill })
    : null;

  // ---- OTP state ----
  const [otpStep, setOtpStep] = useState("idle"); // idle | sending | sent | verifying | verified
  const [otpCode, setOtpCode] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [otpError, setOtpError] = useState("");
  // Pre-warm: when the customer reaches the Review step we fire /otp/send in
  // the background ONCE so the SMS is already in transit while they read the
  // terms. Twilio Verify codes live ~10 min, so clicking "Confirmar identidad"
  // within that window reuses the SMS already sitting on their phone — no
  // double-send, no waiting, no "Reenviar" required.
  const [otpPrewarmedAt, setOtpPrewarmedAt] = useState(0); // unix ms; 0 = not prewarmed

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const phoneDigits = (form.telefono || "").replace(/\D+/g, "");
  const phoneOk = phoneDigits.length === 10;

  // Cooldown timer (seconds) before the customer can re-trigger SMS or
  // request a voice call. Mirrors Twilio Verify's ~30s server-side throttle.
  const [otpCooldown, setOtpCooldown] = useState(0);
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const id = setInterval(() => setOtpCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [otpCooldown]);

  // Funnel step 4/5 — fire ONCE per Enroll mount when the form is rendered
  // (i.e. visitor passed all guards: ESIID verified, plan selected). We
  // gate on `address` to avoid firing while the address-first guard is
  // still in flight.
  useEffect(() => {
    if (!address) return;
    trackEnrollmentStart({ planId: selectedPlan, serviceType });
    // Run exactly once per mount; address/selectedPlan are stable here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- OTP pre-warm: fire silently the moment the customer reaches /review
  // and has a valid phone. We skip if already prewarmed in the last 8 minutes
  // (well within Twilio Verify's 10-min code lifetime).
  useEffect(() => {
    if (step !== "review" || !phoneOk) return;
    const PREWARM_TTL_MS = 8 * 60 * 1000;
    if (otpPrewarmedAt && Date.now() - otpPrewarmedAt < PREWARM_TTL_MS) return;
    let cancelled = false;
    (async () => {
      try {
        await api.post("/otp/send", { phone: phoneDigits, channel: "sms" });
        if (!cancelled) setOtpPrewarmedAt(Date.now());
      } catch (_) {
        // Silent: if pre-warm fails (rate limit, invalid phone, Twilio
        // outage) the manual click on "Confirmar identidad" will retry
        // and surface the error to the user there.
      }
    })();
    return () => { cancelled = true; };
  }, [step, phoneOk, phoneDigits, otpPrewarmedAt]);

  // ---- Form submission (Section 1 + Section 2) → goes to "review" step ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.nombre || !form.apellido || !form.email || !form.fecha_nacimiento || !phoneOk) {
      toast.error("Completa todos los campos obligatorios para continuar.");
      return;
    }
    if (!form.fecha_inicio) {
      toast.error("Elige cuándo quieres que inicie tu servicio.");
      return;
    }
    // Final fraud gate — re-validate email + phone + DOB server-side
    // before moving to review. If anything comes back invalid, the inline
    // error appears under the input and we don't advance.
    const dobOk = validateDobInline(form.fecha_nacimiento);
    const [emailOk, phoneFraudOk] = await Promise.all([
      validateEmailInline(form.email),
      validatePhoneInline(form.telefono),
    ]);
    if (!phoneFraudOk) {
      // Specific toast so the customer immediately knows WHY we won't advance.
      toast.error(phoneError || "No pudimos verificar este número. Intenta con otro para continuar.", { duration: 6000 });
      try {
        document.querySelector('[data-testid="form-telefono"]')?.focus();
        document.querySelector('[data-testid="form-telefono"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch { /* noop */ }
      return;
    }
    if (!emailOk || !dobOk) {
      toast.error("Revisa los campos marcados antes de continuar.");
      return;
    }
    setStep("review");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- OTP send (called from Review step "Confirmar identidad") ----
  const startReviewOtp = async (channel = "sms") => {
    setOtpError("");
    if (!termsAccepted) {
      toast.error("Acepta los términos para continuar.");
      return;
    }
    setShowOtpModal(true);
    setOtpCode("");

    // Fast path: we already pre-warmed the SMS the moment the customer
    // reached the Review step. If it's still fresh (<8 min) AND the
    // customer is asking for the same channel we used to pre-warm (sms),
    // just open the modal in "sent" state — no double SMS, no extra wait.
    const PREWARM_TTL_MS = 8 * 60 * 1000;
    const prewarmFresh = channel === "sms"
      && otpPrewarmedAt
      && (Date.now() - otpPrewarmedAt < PREWARM_TTL_MS);
    if (prewarmFresh) {
      setOtpStep("sent");
      setOtpCooldown(30);
      toast.success("Te enviamos un código por SMS hace unos segundos");
      return;
    }

    setOtpStep("sending");
    try {
      await api.post("/otp/send", { phone: phoneDigits, channel });
      setOtpStep("sent");
      setOtpPrewarmedAt(Date.now());
      setOtpCooldown(30);
      toast.success(channel === "call" ? "Te estamos llamando con el código…" : "Código enviado por SMS");
    } catch (err) {
      setOtpStep("idle");
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "VOIP_BLOCKED") {
        setOtpError(detail.message);
      } else {
        setOtpError(formatApiError(detail) || "No pudimos enviar el código.");
      }
    }
  };

  // ---- OTP verify + lead create (single atomic call from UX perspective) ----
  const verifyAndSubmit = async () => {
    setOtpError("");
    if ((otpCode || "").replace(/\D+/g, "").length < 4) {
      setOtpError("El código debe tener al menos 4 dígitos.");
      return;
    }
    setOtpStep("verifying");
    let token = "";
    try {
      const { data } = await api.post("/otp/verify", { phone: phoneDigits, code: otpCode });
      token = data.verification_token;
      setOtpToken(token);
      setOtpStep("verified");
    } catch (err) {
      setOtpStep("sent");
      setOtpError(formatApiError(err.response?.data?.detail) || "Código incorrecto o expirado.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/leads", {
        ...form,
        plan_seleccionado: selectedPlan,
        plan_rate_cents_kwh: currentPlan.rate,
        service_type: serviceType,
        address,
        unit,
        city,
        state: "TX",
        zip: zip || "00000",
        estimated_monthly_bill: estimate?.estimated_monthly_bill || null,
        annual_kwh: estimate?.annual_kwh || null,
        utility_name: utilityParam || estimate?.utility_name || null,
        esiid: esiidParam || null,
        switch_hold_indicator: (localStorage.getItem("velcor_switch_hold_indicator") || "").toUpperCase(),
        language: localStorage.getItem("velcor_lang") || "es",
        source: "web-enroll",
        referral_code: form.referral_code || "",
        phone_verification_token: token,
      });
      setOrderNumber(data?.order_number || "");
      setSubmitted(true);
      setShowOtpModal(false);
      setStep("submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Funnel step 5/5 — lead submitted successfully. Separate from
      // trackLead (which also fires Google Ads conversion). This event is
      // funnel-only so we can compute step-by-step conversion in GA4
      // without double-counting Ads conversions.
      try {
        trackEnrollmentComplete({
          orderNumber: data?.order_number || "",
          planId: selectedPlan,
          depositUsd: Number(data?.deposit?.amount_usd || 0),
          value: Number(currentPlan?.rate || 0) * (currentPlan?.kwh || 0) / 100,
        });
      } catch { /* noop */ }

      // ----- Google Ads / conversion tracking -----
      // Stash the order payload in sessionStorage and redirect to the
      // dedicated `/enrollment-complete` URL so ad-platform conversion pixels
      // (Google Ads, Meta, GA4) fire on a real navigation event. The
      // ConfirmationView component is rendered identically there — the user
      // perceives no UX change.
      try {
        sessionStorage.setItem(
          ENROLL_COMPLETE_STORAGE_KEY,
          JSON.stringify({
            orderNumber: data?.order_number || "",
            form,
            currentPlan,
            estimate,
            address,
            unit,
            city,
            zip,
            esiidParam: esiidParam || null,
            utilityParam: utilityParam || null,
            // Deposit info from automated risk evaluation. If present and
            // > 0, EnrollmentComplete shows a "Paga tu depósito" CTA that
            // links directly to the Stripe Checkout session.
            deposit: data?.deposit || null,
          })
        );
      } catch (e) {
        // sessionStorage may be unavailable (Safari private mode). The
        // page-level fallback below still shows the in-page confirmation.
      }
      navigate("/enrollment-complete", { replace: true });
    } catch (err) {
      // Surface fraud-block messages clearly (HTTP 403 + code FRAUD_BLOCKED).
      // The backend returns { code: "FRAUD_BLOCKED", reason, message } so we
      // show the localized message verbatim instead of a generic error.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "FRAUD_BLOCKED") {
        setOtpError(detail.message || "No podemos procesar esta solicitud. Llama al (800) 715-6516.");
      } else {
        setOtpError(formatApiError(detail) || "No pudimos completar la activación.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // otpToken is captured for parity with the legacy single-step flow; the
  // server only requires it on the /leads POST which we already include via `token`.
  // eslint-disable-next-line no-unused-vars
  const _otpTokenSnapshot = otpToken;

  // Don't flash the form while the address-guard redirect is in flight.
  if (!esiidParam) return null;

  return (
    <Layout minimal>
      <Helmet>
        <title>{t("enroll.pageTitle")} — Velcor Energy</title>
      </Helmet>

      {/* Full-page clean white background — corporate banking feel */}
      <div className="fixed inset-0 -z-10 bg-white" aria-hidden />

      {/* Mini brand bar — minimal layout hides the public Navbar, so we
          render a slim wordmark + secure indicator instead. */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1180px] mx-auto px-5 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 group" data-testid="enroll-brand">
            <span className="grid place-items-center w-8 h-8 rounded-lg bg-velcor-green text-white shadow-[0_0_18px_rgba(47,122,58,0.4)] group-hover:scale-105 transition-transform">
              <Zap className="w-4 h-4" strokeWidth={2.6} />
            </span>
            <span className="font-black text-[14.5px] tracking-tight text-velcor-ink">VELCOR ENERGY</span>
          </a>
          <a
            href="tel:+18007156516"
            className="hidden sm:inline-flex items-center gap-1.5 text-[12px] font-extrabold text-slate-600 hover:text-velcor-green transition-colors tabular-nums"
            data-testid="enroll-header-phone"
          >
            <Phone className="w-3.5 h-3.5" strokeWidth={2.4} />
            800-715-6516
          </a>
        </div>
      </header>

      <PageBanner
        eyebrow="Inscripción · Velcor Energy"
        title={submitted ? "¡Bienvenido a Velcor!" : "Activa tu servicio eléctrico"}
        subtitle={submitted
          ? "Tu solicitud fue recibida. Revisa tu correo para los próximos pasos."
          : "Información básica + verificación por SMS. Toma menos de 30 segundos."}
        as="h1"
        testId="enroll-banner"
      />

      <section
        className="relative max-w-[1180px] mx-auto px-5 pt-6 md:pt-8 pb-24 bg-white"
        data-testid="enroll-page"
      >
        {/* Back link */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-[13px] font-extrabold text-slate-500 hover:text-velcor-green"
            data-testid="enroll-back"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("enroll.back")}
          </button>
        </div>

        {!submitted && <EnrollStepper currentStep={step} />}

        {submitted ? (
          <ConfirmationView
            orderNumber={orderNumber}
            form={form}
            currentPlan={currentPlan}
            estimate={estimate}
            address={address}
            unit={unit}
            city={city}
            zip={zip}
            esiidParam={esiidParam}
            utilityParam={utilityParam}
          />
        ) : step === "review" ? (
          <ReviewView
            form={form}
            currentPlan={currentPlan}
            address={address}
            unit={unit}
            city={city}
            zip={zip}
            esiidParam={esiidParam}
            utilityParam={utilityParam}
            estimate={estimate}
            termsAccepted={termsAccepted}
            setTermsAccepted={setTermsAccepted}
            onBack={() => setStep("datos")}
            onContinue={startReviewOtp}
          />
        ) : (
          <div className="flex flex-col lg:grid lg:grid-cols-[1fr_380px] gap-8">
            {/* LEFT — Form (corporate 3-section layout) */}
            <form onSubmit={handleSubmit} className="space-y-6 min-w-0" data-testid="enroll-form">
              {/* Section 1 — Personal */}
              <motion.fieldset
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
                className="border-0 p-0 m-0 pb-2 border-b border-slate-200/70"
              >
                <legend className="px-0 text-[10.5px] font-black uppercase tracking-[0.18em] text-velcor-green">
                  {t("enroll.section1")}
                </legend>
                <h2
                  className="font-bold text-[22px] text-velcor-ink mt-1 mb-6"
                  style={{ letterSpacing: "-0.018em" }}
                >
                  {t("enroll.section1Title")}
                </h2>

                <div className="space-y-1">
                  {/* Row 1 — Nombre + Apellido */}
                  <div className="grid md:grid-cols-2 gap-4 py-5">
                    <Field
                      icon={User}
                      label={t("form.firstName")}
                      required
                      value={form.nombre}
                      onChange={(v) => update("nombre", v)}
                      placeholder={t("enroll.firstNameExample")}
                      testid="form-nombre"
                    />
                    <Field
                      icon={User}
                      label={t("form.lastName")}
                      required
                      value={form.apellido}
                      onChange={(v) => update("apellido", v)}
                      placeholder={t("enroll.lastNameExample")}
                      testid="form-apellido"
                    />
                  </div>

                  {/* Row 2 — Email + Fecha nacimiento */}
                  <div className="grid md:grid-cols-2 gap-4 py-5">
                    <Field
                      icon={Mail}
                      label={t("form.email")}
                      required
                      type="email"
                      placeholder="tunombre@email.com"
                      value={form.email}
                      onChange={(v) => { update("email", v); if (emailError) setEmailError(""); }}
                      onBlur={(v) => validateEmailInline(v)}
                      error={emailError}
                      testid="form-email"
                    />
                    <Field
                      icon={Calendar}
                      label={t("form.dob")}
                      required
                      type="date"
                      placeholder="MM/DD/YYYY"
                      value={form.fecha_nacimiento}
                      onChange={(v) => { update("fecha_nacimiento", v); if (dobError) setDobError(""); validateDobInline(v); }}
                      onBlur={(v) => validateDobInline(v)}
                      error={dobError}
                      testid="form-fecha-nacimiento"
                    />
                  </div>

                  {/* Row 3 — Teléfono (smaller width on desktop, full-width on mobile) */}
                  <div className="py-5">
                    <div className="w-full sm:max-w-[280px]">
                      <Field
                        icon={Phone}
                        label={t("form.phone")}
                        required
                        type="tel"
                        placeholder="(713) 000-0000"
                        value={form.telefono}
                        // Phone must have exactly 10 digits (US format).
                        // We strip every non-digit BEFORE saving so users can
                        // type freely "(713) 555-1234" or "713-555-1234" and
                        // we always end up with up to 10 digits, no more.
                        maxLength={14}
                        onChange={(v) => {
                          const digits = (v || "").replace(/\D+/g, "").slice(0, 10);
                          // Format back nicely as the user types: (xxx) xxx-xxxx
                          let formatted = digits;
                          if (digits.length > 6) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
                          else if (digits.length > 3) formatted = `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
                          else if (digits.length > 0) formatted = `(${digits}`;
                          update("telefono", formatted);
                          if (phoneError) setPhoneError("");
                          if (otpStep === "verified") {
                            setOtpStep("idle");
                            setOtpToken("");
                          }
                        }}
                        onBlur={(v) => validatePhoneInline(v)}
                        error={phoneError}
                        testid="form-telefono"
                      />
                      {!phoneError && (
                        <p className="mt-1.5 text-[11.5px] text-slate-500">
                          {phoneChecking ? "Verificando número…" : "10 dígitos. Te enviaremos un código por SMS."}
                        </p>
                      )}
                    </div>
                    <div className="mt-4">
                      <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-2">
                        {t("form.phoneIsContact")} <span className="text-rose-500 normal-case">*</span>
                      </label>
                      <div className="flex items-center gap-5" data-testid="phone-is-contact-row">
                        {[
                          { value: true, label: "Sí" },
                          { value: false, label: "No" },
                        ].map((opt) => {
                          const checked = (form.phone_is_contact ?? true) === opt.value;
                          return (
                            <label
                              key={String(opt.value)}
                              className="inline-flex items-center gap-2 cursor-pointer select-none group"
                              data-testid={`phone-is-contact-${opt.value ? "yes" : "no"}`}
                            >
                              <input
                                type="radio"
                                name="phone_is_contact"
                                checked={checked}
                                onChange={() => update("phone_is_contact", opt.value)}
                                className="sr-only peer"
                              />
                              <span
                                aria-hidden
                                className={`grid place-items-center w-5 h-5 rounded-full border-2 transition-colors ${
                                  checked
                                    ? "border-velcor-green bg-white"
                                    : "border-slate-300 bg-white group-hover:border-slate-400"
                                }`}
                              >
                                <span
                                  className={`w-2.5 h-2.5 rounded-full bg-velcor-green transition-transform duration-150 ${
                                    checked ? "scale-100" : "scale-0"
                                  }`}
                                />
                              </span>
                              <span className="text-[14px] font-semibold text-slate-700">
                                {opt.label}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Row 4 — Referral code (siempre visible para no pasar desapercibido). */}
                  <div className="py-5">
                    <div>
                      <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1.5">
                        Código de referido{" "}
                        <span className="text-slate-400 normal-case font-normal">· opcional</span>
                      </label>
                      <div className="relative w-full sm:max-w-[280px]">
                        <UserCheck className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-velcor-green" />
                        <input
                          type="text"
                          value={form.referral_code}
                          onChange={(e) =>
                            update("referral_code", e.target.value.toUpperCase().slice(0, 24))
                          }
                          placeholder="VEL-"
                          className="w-full h-11 pl-10 pr-3 rounded-xl border border-slate-200 bg-white text-[14px] text-velcor-ink font-mono tracking-[0.04em] focus:outline-none focus:border-velcor-green focus:ring-2 focus:ring-velcor-green/15"
                          data-testid="form-referral-code"
                        />
                      </div>
                      <p className="text-[11.5px] text-slate-500 mt-1.5 leading-snug">
                        Si te invitó un promotor o amigo, ingresa su código aquí (ej. VEL- ).
                      </p>
                    </div>
                  </div>
                </div>
              </motion.fieldset>

              {/* Section 2 — Service details */}
              <motion.fieldset
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.05 }}
                className="border-0 p-0 m-0 pb-2"
              >
                <legend className="px-0 text-[10.5px] font-black uppercase tracking-[0.18em] text-velcor-green">
                  {t("enroll.section2")}
                </legend>
                <h2
                  className="font-bold text-[22px] text-velcor-ink mt-1 mb-6"
                  style={{ letterSpacing: "-0.018em" }}
                >
                  {t("enroll.section2Title")}
                </h2>

                <div className="w-full sm:max-w-[300px]">
                  <BusinessDayPicker
                    label={t("form.startDate")}
                    value={form.fecha_inicio}
                    onChange={(v) => update("fecha_inicio", v)}
                    lang={localStorage.getItem("velcor_lang") || "es"}
                    testid="form-fecha_inicio"
                  />
                </div>
              </motion.fieldset>

              {/* Submit → Review step */}
              <div className="pt-8 md:pt-10">
                <button
                  type="submit"
                  disabled={submitting || phoneChecking || !!phoneError || !phoneOk}
                  className="btn-green w-full h-14 text-[15px] disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="enroll-submit"
                  title={phoneError || (phoneChecking ? "Verificando número…" : "")}
                >
                  Revisar y continuar
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>

                <div className="mt-5 flex flex-wrap items-center justify-center gap-4 text-[12px] font-bold text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-velcor-green" />
                    {t("enroll.trustSecure")}
                  </span>
                  <span className="opacity-30">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-velcor-green" />
                    {t("enroll.trustGuarantee")}
                  </span>
                  <span className="opacity-30">·</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-velcor-green" />
                    {t("enroll.trustActivation")}
                  </span>
                </div>
              </div>
            </form>

            {/* RIGHT — Plan summary sticky */}
            <EnrollSidebar
              currentPlan={currentPlan}
              estimate={estimate}
              address={address}
              unit={unit}
              city={city}
              zip={zip}
              esiidParam={esiidParam}
              utilityParam={utilityParam}
            />
          </div>
        )}
      </section>

      {/* ===== OTP Verification Modal ===== */}
      {showOtpModal && (
        <OtpModal
          phoneDigits={phoneDigits}
          otpCode={otpCode}
          setOtpCode={setOtpCode}
          otpStep={otpStep}
          otpError={otpError}
          otpCooldown={otpCooldown}
          submitting={submitting}
          onClose={() => setShowOtpModal(false)}
          onResendSms={() => {
            setOtpCode("");
            startReviewOtp("sms");
          }}
          onResendCall={() => {
            setOtpCode("");
            startReviewOtp("call");
          }}
          onVerify={verifyAndSubmit}
        />
      )}
    </Layout>
  );
}
